import api from "./api";

// Base URL for fuel reports API
const FUEL_REPORTS_BASE_URL = "/fuel-reports";

// Types for fuel reports
export interface FuelReportFilters {
  dateFilter?: string;
  startDate?: string;
  endDate?: string;
  vehicleModels?: string;
  vehicleTypes?: string;
  vehicleAge?: string;
  districts?: string;
  zones?: string;
  regions?: string;
  staffTypes?: string;
  vehicleNumbers?: string;
}

export interface MonthOnMonthFilters {
  monthsCount?: number;
  vehicleModels?: string;
  vehicleTypes?: string;
  vehicleAge?: string;
  districts?: string;
  zones?: string;
  regions?: string;
  staffTypes?: string;
  vehicleNumbers?: string;
}

export interface ExportFilters extends FuelReportFilters {
  reportType: string;
}

export interface VehicleConsumptionData {
  vehicleNumber: string;
  model: string;
  vehicleType: string;
  vehicle_age: number;
  age_category: string;
  district: string;
  zone: string;
  region: string;
  staff_name: string;
  staff_type: string;
  fuel_transactions: number;
  total_fuel_consumed_liters: number;
  total_fuel_cost_inr: number;
  avg_fuel_price_per_liter: number;
  distance_traveled_km: number;
  fuel_efficiency_kmpl: number;
  efficiency_rating: string;
  cost_per_km: number;
}

export interface VehicleConsumptionSummary {
  total_vehicles: number;
  total_fuel_consumed: number;
  total_fuel_cost: number;
  avg_efficiency: number;
  efficiency_distribution: {
    excellent: number;
    good: number;
    average: number;
    poor: number;
    no_data: number;
  };
}

export interface VehicleConsumptionResponse {
  isSuccess: boolean;
  message: string;
  data: {
    summary: VehicleConsumptionSummary;
    data: VehicleConsumptionData[];
    filters_applied: FuelReportFilters;
    report_generated_at: string;
  };
}

export interface RegionalAnalysisItem {
  region: string;
  zone: string;
  district: string;
  model: string;
  vehicleType: string;
  age_category: string;
  total_vehicles: number;
  total_fuel_consumed_liters: number;
  total_fuel_cost_inr: number;
  fuel_transactions: number;
  avg_fuel_per_vehicle: number;
  avg_cost_per_vehicle: number;
  avg_price_per_liter: number;
}

export interface RegionalAnalysisResponse {
  isSuccess: boolean;
  message: string;
  data: {
    summary: {
      total_regions: number;
      total_fuel_consumed: number;
      total_fuel_cost: number;
    };
    data: Record<
      string,
      Record<string, Record<string, RegionalAnalysisItem[]>>
    >;
    flat_data: RegionalAnalysisItem[];
    filters_applied: FuelReportFilters;
    report_generated_at: string;
  };
}

export interface StaffPerformanceItem {
  staff_name: string;
  staff_type: string;
  staff_contact: string;
  assignmentType: string;
  district: string;
  zone: string;
  region: string;
  vehicleNumber: string;
  model: string;
  vehicleType: string;
  age_category: string;
  total_fuel_transactions: number;
  total_fuel_consumed_liters: number;
  total_fuel_cost_inr: number;
  avg_fuel_price_per_liter: number;
  avg_cost_per_transaction: number;
  avg_fuel_per_transaction: number;
  consumption_category: string;
}

export interface StaffPerformanceResponse {
  isSuccess: boolean;
  message: string;
  data: {
    summary: {
      total_staff_with_vehicles: number;
      total_fuel_consumed: number;
      total_fuel_cost: number;
      consumption_distribution: {
        high: number;
        medium: number;
        regular: number;
        low: number;
        none: number;
      };
    };
    data_by_staff_type: Record<string, StaffPerformanceItem[]>;
    flat_data: StaffPerformanceItem[];
    filters_applied: FuelReportFilters;
    report_generated_at: string;
  };
}

export interface ModelEfficiencyItem {
  model: string;
  vehicleType: string;
  age_category: string;
  vehicle_count: number;
  total_fuel_consumed_liters: number;
  total_fuel_cost_inr: number;
  fuel_transactions: number;
  avg_fuel_per_vehicle: number;
  avg_cost_per_vehicle: number;
  avg_price_per_liter: number;
  avg_transactions_per_vehicle: number;
  consumption_rating: string;
}

export interface ModelEfficiencyResponse {
  isSuccess: boolean;
  message: string;
  data: {
    summary: {
      total_models: number;
      total_vehicles: number;
      total_fuel_consumed: number;
      total_fuel_cost: number;
    };
    data_by_model: Record<string, Record<string, ModelEfficiencyItem[]>>;
    flat_data: ModelEfficiencyItem[];
    filters_applied: FuelReportFilters;
    report_generated_at: string;
  };
}

export interface SourceComparisonItem {
  fuel_source: string;
  vehicles_served: number;
  total_transactions: number;
  total_fuel_liters: number;
  total_cost: number;
  avg_price_per_liter: number;
  earliest_transaction: string;
  latest_transaction: string;
}

export interface SourceComparisonResponse {
  isSuccess: boolean;
  message: string;
  data: {
    summary: {
      total_sources: number;
      total_vehicles_served: number;
      total_transactions: number;
      total_fuel_consumed: number;
      total_cost: number;
    };
    data: SourceComparisonItem[];
    filters_applied: Pick<
      FuelReportFilters,
      "dateFilter" | "startDate" | "endDate"
    >;
    report_generated_at: string;
  };
}

export interface MonthlyData {
  month: string;
  month_key: string;
  active_vehicles: number;
  total_fuel_consumed: number;
  total_fuel_cost: number;
  total_transactions: number;
  avg_fuel_per_vehicle: number;
  avg_cost_per_vehicle: number;
  avg_price_per_liter: number;
  month_on_month_change: {
    fuel_consumed_change_percent: number;
    fuel_cost_change_percent: number;
    active_vehicles_change_percent: number;
  };
}

export interface MonthOnMonthResponse {
  isSuccess: boolean;
  message: string;
  data: {
    summary: {
      months_analyzed: number;
      total_fuel_consumed: number;
      total_fuel_cost: number;
      avg_monthly_fuel: number;
      avg_monthly_cost: number;
      trend_direction: string;
    };
    monthly_data: MonthlyData[];
    filters_applied: MonthOnMonthFilters;
    report_generated_at: string;
  };
}

export interface DashboardAnalyticsResponse {
  isSuccess: boolean;
  message: string;
  data: {
    overview: {
      total_vehicles: number;
      total_fuel_consumed: number;
      total_fuel_cost: number;
      avg_efficiency: number;
      efficiency_distribution: Record<string, number>;
      data_sources: number;
      reporting_period: Record<string, any>;
    };
    vehicle_insights: {
      top_consumers: VehicleConsumptionData[];
      efficiency_leaders: VehicleConsumptionData[];
      cost_efficiency: VehicleConsumptionData[];
    };
    regional_insights: {
      regional_summary: Record<string, any>;
      top_consuming_regions: RegionalAnalysisItem[];
    };
    /** Omitted by slim dashboard API; optional for typing older responses. */
    staff_insights?: {
      staff_summary: Record<string, any>;
      top_staff_performers: StaffPerformanceItem[];
    };
    model_insights?: {
      model_summary: Record<string, any>;
      efficiency_by_age: ModelEfficiencyItem[];
    };
    fuel_sources?: SourceComparisonItem[];
    month_on_month_trends: {
      summary: {
        months_analyzed: number;
        total_fuel_consumed: number;
        avg_monthly_fuel: number;
        trend_direction: string;
      };
      recent_months: MonthlyData[];
    };
    filter_options?: FilterOptionsResponse["data"];
  };
}

export interface FilterOptionsResponse {
  isSuccess: boolean;
  message: string;
  data: {
    vehicle_models: string[];
    vehicle_types: string[];
    vehicle_numbers: string[];
    vehicle_ages: {
      new: number;
      medium: number;
      old: number;
      very_old: number;
    };
    staff_types: string[];
    regions: string[];
    zones: string[];
    districts: string[];
    date_filters: string[];
  };
}

export class FuelReportsService {
  // 1. Vehicle Fuel Consumption Report
  static async getVehicleConsumption(
    filters: FuelReportFilters,
    options?: { summaryOnly?: boolean }
  ): Promise<VehicleConsumptionResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    if (options?.summaryOnly) {
      params.append("summaryOnly", "true");
    }

    const response = await api.get(
      `${FUEL_REPORTS_BASE_URL}/vehicle-consumption?${params}`
    );
    return response.data;
  }

  static async getFuelConsumptionDashboard(
    filters: FuelReportFilters
  ): Promise<VehicleConsumptionResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const response = await api.get(
      `${FUEL_REPORTS_BASE_URL}/vehicle-consumption-dashboard?${params}`
    );
    return response.data;
  }

  // 2. Regional Fuel Analysis Report
  static async getRegionalAnalysis(
    filters: FuelReportFilters
  ): Promise<RegionalAnalysisResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const response = await api.get(
      `${FUEL_REPORTS_BASE_URL}/regional-analysis?${params}`
    );
    return response.data;
  }

  // 3. Staff-Vehicle Fuel Performance Report
  static async getStaffPerformance(
    filters: FuelReportFilters
  ): Promise<StaffPerformanceResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const response = await api.get(
      `${FUEL_REPORTS_BASE_URL}/staff-performance?${params}`
    );
    return response.data;
  }

  // 4. Model & Age-wise Fuel Efficiency Report
  static async getModelEfficiency(
    filters: FuelReportFilters
  ): Promise<ModelEfficiencyResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const response = await api.get(
      `${FUEL_REPORTS_BASE_URL}/model-efficiency?${params}`
    );
    return response.data;
  }

  // 5. Fuel Source Comparison Report
  static async getSourceComparison(
    filters: Pick<FuelReportFilters, "dateFilter" | "startDate" | "endDate">
  ): Promise<SourceComparisonResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const response = await api.get(
      `${FUEL_REPORTS_BASE_URL}/source-comparison?${params}`
    );
    return response.data;
  }

  // 6. Month-on-Month Fuel Analysis Report
  static async getMonthOnMonth(
    filters: MonthOnMonthFilters
  ): Promise<MonthOnMonthResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value.toString());
    });

    const response = await api.get(
      `${FUEL_REPORTS_BASE_URL}/month-on-month?${params}`
    );
    return response.data;
  }

  // 7. Dashboard Analytics
  static async getDashboardAnalytics(
    filters: Pick<FuelReportFilters, "dateFilter" | "startDate" | "endDate">
  ): Promise<DashboardAnalyticsResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const response = await api.get(
      `${FUEL_REPORTS_BASE_URL}/dashboard?${params}`
    );
    return response.data;
  }

  // 8. Filter Options
  static async getFilterOptions(): Promise<FilterOptionsResponse> {
    const response = await api.get(`${FUEL_REPORTS_BASE_URL}/filter-options`);
    return response.data;
  }

  // 9. Export to CSV
  static async exportReport(filters: ExportFilters): Promise<Blob> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const response = await api.get(
      `${FUEL_REPORTS_BASE_URL}/export?${params}`,
      {
        responseType: "blob",
      }
    );
    return response.data;
  }

  // Utility function to download CSV
  static downloadCSV(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // get vehicle report based on fuel consumption and total distance traveled
  static async getVehicleReportBasedOnFuelConsumptionAndTotalDistanceTraveled(
    filters: FuelReportFilters
  ): Promise<any> {
    const params = new URLSearchParams({
      dateFilter: filters.dateFilter || "last30days",
      startDate: filters.startDate || "",
      endDate: filters.endDate || "today",
      vehicleModels: filters.vehicleModels || "",
      vehicleTypes: filters.vehicleTypes || "",
      vehicleAge: filters.vehicleAge || "all",
      districts: filters.districts || "",
      zones: filters.zones || "",
      regions: filters.regions || "",
      staffTypes: filters.staffTypes || "",
      vehicleNumbers: filters.vehicleNumbers || "",
    });

    const response = await api.get(
      `/gps-reports/vehicle-report-based-on-gps-reading-and-fuel-consumption?${params}`
    );
    return response.data;
  }
}
